import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Users, CreditCard, Mail, Search, Eye, XCircle, LogIn, TrendingUp, DollarSign, BarChart3, Activity, FileText, UserMinus, ExternalLink, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [dudaLocationId, setDudaLocationId] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [userToCancel, setUserToCancel] = useState(null);
  
  // Date range state
  const [dateRange, setDateRange] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate, dateRange, startDate, endDate]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Build query params for date filtering
      let dateParams = '';
      if (dateRange !== 'all') {
        if (dateRange === 'custom' && startDate && endDate) {
          dateParams = `&start_date=${startDate}&end_date=${endDate}`;
        } else {
          dateParams = `&date_range=${dateRange}`;
        }
      }
      
      const [usersRes, leadsRes, subsRes, metricsRes, cancellationsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/users?admin_id=${user.id}${dateParams}`),
        fetch(`${API_URL}/api/admin/leads?admin_id=${user.id}${dateParams}`),
        fetch(`${API_URL}/api/admin/subscriptions?admin_id=${user.id}${dateParams}`),
        fetch(`${API_URL}/api/admin/metrics?admin_id=${user.id}${dateParams}`),
        fetch(`${API_URL}/api/admin/cancellations?admin_id=${user.id}${dateParams}`)
      ]);

      if (!usersRes.ok) {
        const err = await usersRes.json();
        throw new Error(err.detail || 'Access denied');
      }

      const [usersData, leadsData, subsData, metricsData, cancellationsData] = await Promise.all([
        usersRes.json(),
        leadsRes.json(),
        subsRes.json(),
        metricsRes.json(),
        cancellationsRes.json()
      ]);

      setUsers(usersData.users || []);
      setLeads(leadsData.leads || []);
      setSubscribers(subsData.subscribers || []);
      setMetrics(metricsData);
      setCancellations(cancellationsData.cancellations || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/user/${userId}/details?admin_id=${user.id}`);
      if (!res.ok) throw new Error('Failed to load user details');
      const data = await res.json();
      setUserDetails(data);
      setSelectedUser(userId);
      setDudaLocationId(data.user?.duda_location_id || '');
      setShowUserModal(true);
    } catch (err) {
      alert(err.message);
    }
  };

  const openCancelModal = (u) => {
    setUserToCancel(u);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const cancelSubscription = async () => {
    if (!userToCancel) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/user/${userToCancel.id}/cancel-subscription?admin_id=${user.id}&reason=${encodeURIComponent(cancelReason || 'No reason provided')}`, {
        method: 'POST'
      });
      if (!res.ok) throw new Error('Failed to cancel subscription');
      alert('Subscription cancelled');
      setShowCancelModal(false);
      setUserToCancel(null);
      loadData();
      setShowUserModal(false);
    } catch (err) {
      alert(err.message);
    }
  };

  const updatePlan = async (userId, plan) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/user/${userId}/update-plan?admin_id=${user.id}&plan=${plan}`, {
        method: 'POST'
      });
      if (!res.ok) throw new Error('Failed to update plan');
      alert(`Plan updated to ${plan}`);
      loadData();
      viewUserDetails(userId);
    } catch (err) {
      alert(err.message);
    }
  };

  const setDudaLocation = async (userId) => {
    if (!dudaLocationId.trim()) {
      alert('Please enter a Location ID');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/admin/user/${userId}/set-duda?admin_id=${user.id}&location_id=${dudaLocationId}`, {
        method: 'POST'
      });
      if (!res.ok) throw new Error('Failed to set Duda location');
      alert('Duda Location ID saved');
      viewUserDetails(userId);
    } catch (err) {
      alert(err.message);
    }
  };

  const impersonateUser = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/impersonate/${userId}?admin_id=${user.id}`, {
        method: 'POST'
      });
      if (!res.ok) throw new Error('Failed to impersonate');
      const data = await res.json();
      
      localStorage.setItem('impersonation_token', data.token);
      localStorage.setItem('impersonating_user', JSON.stringify(data.user));
      alert(`Now viewing as ${data.user.name}. Refresh the page to see their dashboard.`);
      window.location.reload();
    } catch (err) {
      alert(err.message);
    }
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredLeads = leads.filter(l =>
    l.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gray-900 text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="hover:bg-gray-800 p-2 rounded">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold">Admin Dashboard</h1>
          </div>
          <div className="text-sm text-gray-400">
            Logged in as {user?.email}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'overview' ? 'bg-orange-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
          >
            📊 Overview
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'users' ? 'bg-orange-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
          >
            👥 Users ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('subscribers')}
            className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'subscribers' ? 'bg-orange-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
          >
            💳 Subscribers ({subscribers.length})
          </button>
          <button
            onClick={() => setActiveTab('leads')}
            className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'leads' ? 'bg-orange-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
          >
            📧 Leads ({leads.length})
          </button>
          <button
            onClick={() => setActiveTab('cancellations')}
            className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'cancellations' ? 'bg-orange-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
          >
            🚪 Cancellations ({cancellations.length})
          </button>
          <button
            onClick={() => setActiveTab('traffic')}
            className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'traffic' ? 'bg-orange-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
          >
            📈 Traffic
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && metrics && (
          <div className="space-y-8">
            {/* Date Range Selector */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gray-500" />
                  <span className="font-medium text-gray-700">Date Range:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'all', label: 'All Time' },
                    { value: '7d', label: 'Last 7 Days' },
                    { value: '30d', label: 'Last 30 Days' },
                    { value: '90d', label: 'Last 90 Days' },
                    { value: 'custom', label: 'Custom' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setDateRange(option.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        dateRange === option.value
                          ? 'bg-orange-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                {dateRange === 'custom' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                    />
                    <span className="text-gray-500">to</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Revenue Stats */}
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-4">💰 Revenue</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-8 h-8 opacity-80" />
                    <div>
                      <p className="text-green-100 text-sm">Monthly Recurring Revenue</p>
                      <p className="text-3xl font-bold">${metrics.mrr}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <p className="text-sm text-gray-600">Annual Run Rate</p>
                  <p className="text-2xl font-bold text-gray-900">${metrics.arr.toLocaleString()}</p>
                </div>
                
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <p className="text-sm text-gray-600">Starter Revenue</p>
                  <p className="text-2xl font-bold text-blue-600">${metrics.revenue_starter}</p>
                  <p className="text-xs text-gray-500">{metrics.starter_users} × $27</p>
                </div>
                
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <p className="text-sm text-gray-600">Pro Revenue</p>
                  <p className="text-2xl font-bold text-orange-600">${metrics.revenue_pro}</p>
                  <p className="text-xs text-gray-500">{metrics.pro_users} × $67</p>
                </div>
              </div>
            </div>

            {/* Subscription Stats */}
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-4">📊 Subscriptions</h2>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Total Users</p>
                      <p className="text-2xl font-bold text-gray-900">{metrics.total_users}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <p className="text-sm text-gray-600">Free</p>
                  <p className="text-2xl font-bold text-gray-500">{metrics.free_users}</p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div className="bg-gray-500 h-2 rounded-full" style={{width: `${(metrics.free_users / metrics.total_users) * 100}%`}}></div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <p className="text-sm text-gray-600">Starter ($27)</p>
                  <p className="text-2xl font-bold text-blue-600">{metrics.starter_users}</p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{width: `${(metrics.starter_users / metrics.total_users) * 100}%`}}></div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <p className="text-sm text-gray-600">Pro ($67)</p>
                  <p className="text-2xl font-bold text-orange-600">{metrics.pro_users}</p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div className="bg-orange-600 h-2 rounded-full" style={{width: `${(metrics.pro_users / metrics.total_users) * 100}%`}}></div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <p className="text-sm text-gray-600">Paid Total</p>
                  <p className="text-2xl font-bold text-green-600">{metrics.total_paid_subscribers}</p>
                </div>
              </div>
            </div>

            {/* Growth & Activity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-4">📈 Growth (This Month)</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <p className="text-sm text-gray-600">New Signups</p>
                    <p className="text-2xl font-bold text-gray-900">{metrics.new_signups_month}</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <p className="text-sm text-gray-600">New Leads</p>
                    <p className="text-2xl font-bold text-purple-600">{metrics.leads_this_month}</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <p className="text-sm text-gray-600">Blogs Generated</p>
                    <p className="text-2xl font-bold text-blue-600">{metrics.blogs_this_month}</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <p className="text-sm text-gray-600">Total Blogs (All Time)</p>
                    <p className="text-2xl font-bold text-gray-600">{metrics.total_blogs}</p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-4">🚪 Churn</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <p className="text-sm text-gray-600">Cancellations (Month)</p>
                    <p className="text-2xl font-bold text-red-600">{metrics.cancellations_this_month}</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <p className="text-sm text-gray-600">Churn Rate</p>
                    <p className="text-2xl font-bold text-red-600">{metrics.churn_rate}%</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <p className="text-sm text-gray-600">Active (7 days)</p>
                    <p className="text-2xl font-bold text-green-600">{metrics.active_users_7d}</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <p className="text-sm text-gray-600">Active (30 days)</p>
                    <p className="text-2xl font-bold text-green-500">{metrics.active_users_30d}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <>
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by email or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">User</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Plan</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Usage (Blogs)</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Usage (Images)</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Discoveries</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Referral</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Joined</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => {
                    // Calculate usage limits based on plan
                    const planLimits = {
                      free: { blogs: 5, images: 5, discoveries: 3 },
                      starter: { blogs: 30, images: 30, discoveries: 5 },
                      pro: { blogs: 90, images: 90, discoveries: 20 }
                    };
                    const limits = planLimits[u.plan] || planLimits.free;
                    const blogsUsed = u.monthly_blogs_used || u.posts_generated || 0;
                    const imagesUsed = u.monthly_images_used || 0;
                    const discoveriesUsed = u.monthly_discoveries_used || 0;
                    
                    return (
                    <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{u.name}</p>
                          <p className="text-sm text-gray-500">{u.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          u.plan === 'pro' ? 'bg-orange-100 text-orange-700' :
                          u.plan === 'starter' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {u.plan?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${blogsUsed >= limits.blogs ? 'text-red-600' : 'text-gray-700'}`}>
                            {blogsUsed}/{limits.blogs}
                          </span>
                          <div className="w-16 bg-gray-200 rounded-full h-1.5">
                            <div 
                              className={`h-1.5 rounded-full ${blogsUsed >= limits.blogs ? 'bg-red-500' : 'bg-green-500'}`}
                              style={{width: `${Math.min((blogsUsed / limits.blogs) * 100, 100)}%`}}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${imagesUsed >= limits.images ? 'text-red-600' : 'text-gray-700'}`}>
                            {imagesUsed}/{limits.images}
                          </span>
                          <div className="w-16 bg-gray-200 rounded-full h-1.5">
                            <div 
                              className={`h-1.5 rounded-full ${imagesUsed >= limits.images ? 'bg-red-500' : 'bg-blue-500'}`}
                              style={{width: `${Math.min((imagesUsed / limits.images) * 100, 100)}%`}}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-700">
                          {discoveriesUsed}/{limits.discoveries}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {u.referral_source ? (
                          <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs font-medium capitalize">
                            {u.referral_source.replace(/_/g, ' ')}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => viewUserDetails(u.id)}
                            className="p-2 hover:bg-gray-100 rounded text-gray-600"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => impersonateUser(u.id)}
                            className="p-2 hover:bg-blue-100 rounded text-blue-600"
                            title="Login as User"
                          >
                            <LogIn className="w-4 h-4" />
                          </button>
                          {u.plan !== 'free' && (
                            <button
                              onClick={() => openCancelModal(u)}
                              className="p-2 hover:bg-red-100 rounded text-red-600"
                              title="Cancel Subscription"
                            >
                              <UserMinus className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">User</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Plan</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">MRR</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Stripe ID</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      No paid subscribers yet
                    </td>
                  </tr>
                ) : (
                  subscribers.map((u) => (
                    <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{u.name}</p>
                          <p className="text-sm text-gray-500">{u.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          u.plan === 'pro' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {u.plan?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-semibold text-green-600">
                        ${u.plan === 'pro' ? '67' : '27'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                        {u.stripe_subscription_id ? `${u.stripe_subscription_id.substring(0, 20)}...` : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => openCancelModal(u)}
                          className="px-3 py-1 text-red-600 border border-red-300 rounded hover:bg-red-50 text-sm"
                        >
                          Cancel
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <>
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Email</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Source</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="px-6 py-12 text-center text-gray-500">
                        No leads yet
                      </td>
                    </tr>
                  ) : (
                    filteredLeads.map((l, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium text-gray-900">{l.email}</td>
                        <td className="px-6 py-4 text-gray-600">{l.source || 'Lead Magnet'}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {l.created_at ? new Date(l.created_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Cancellations Tab */}
        {activeTab === 'cancellations' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">User</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Previous Plan</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Reason</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Cancelled</th>
                </tr>
              </thead>
              <tbody>
                {cancellations.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                      No cancellations yet 🎉
                    </td>
                  </tr>
                ) : (
                  cancellations.map((c, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{c.name}</p>
                          <p className="text-sm text-gray-500">{c.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          c.previous_plan === 'pro' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {c.previous_plan?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600 text-sm">{c.reason || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {c.cancelled_at ? new Date(c.cancelled_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Traffic Tab */}
        {activeTab === 'traffic' && (
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <div className="text-center py-12">
              <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Traffic Analytics</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Connect Google Analytics or Plausible to see page views, unique visitors, and referring sources.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-6 max-w-lg mx-auto text-left">
                <h4 className="font-semibold text-gray-900 mb-3">To enable traffic stats:</h4>
                <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
                  <li>Create a <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">Google Analytics <ExternalLink className="w-3 h-3 inline" /></a> or <a href="https://plausible.io" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">Plausible <ExternalLink className="w-3 h-3 inline" /></a> account</li>
                  <li>Get your tracking code/script</li>
                  <li>Add it to your QuillFire domain</li>
                  <li>Data will appear here within 24-48 hours</li>
                </ol>
              </div>
              
              <div className="mt-8 grid grid-cols-3 gap-4 max-w-lg mx-auto opacity-50">
                <div className="bg-gray-100 rounded-lg p-4">
                  <p className="text-2xl font-bold text-gray-400">-</p>
                  <p className="text-xs text-gray-500">Page Views</p>
                </div>
                <div className="bg-gray-100 rounded-lg p-4">
                  <p className="text-2xl font-bold text-gray-400">-</p>
                  <p className="text-xs text-gray-500">Unique Visitors</p>
                </div>
                <div className="bg-gray-100 rounded-lg p-4">
                  <p className="text-2xl font-bold text-gray-400">-</p>
                  <p className="text-xs text-gray-500">Avg. Session</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {showUserModal && userDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">User Details</h2>
              <button onClick={() => setShowUserModal(false)} className="p-2 hover:bg-gray-100 rounded">
                <XCircle className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Account Info</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Name</p>
                    <p className="font-medium">{userDetails.user?.name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Email</p>
                    <p className="font-medium">{userDetails.user?.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Plan</p>
                    <p className="font-medium capitalize">{userDetails.user?.plan}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Joined</p>
                    <p className="font-medium">{userDetails.user?.created_at ? new Date(userDetails.user.created_at).toLocaleDateString() : '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Referral Source</p>
                    <p className="font-medium capitalize">{userDetails.user?.referral_source?.replace(/_/g, ' ') || 'Not specified'}</p>
                  </div>
                </div>
              </div>

              {/* Plan Management */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Manage Plan</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => updatePlan(selectedUser, 'free')}
                    className={`px-3 py-2 rounded border ${userDetails.user?.plan === 'free' ? 'bg-gray-200 border-gray-300' : 'border-gray-300 hover:bg-gray-50'}`}
                  >
                    Free
                  </button>
                  <button
                    onClick={() => updatePlan(selectedUser, 'starter')}
                    className={`px-3 py-2 rounded border ${userDetails.user?.plan === 'starter' ? 'bg-blue-100 border-blue-300' : 'border-gray-300 hover:bg-gray-50'}`}
                  >
                    Starter ($27)
                  </button>
                  <button
                    onClick={() => updatePlan(selectedUser, 'pro')}
                    className={`px-3 py-2 rounded border ${userDetails.user?.plan === 'pro' ? 'bg-orange-100 border-orange-300' : 'border-gray-300 hover:bg-gray-50'}`}
                  >
                    Pro ($67)
                  </button>
                </div>
              </div>

              {/* Duda Location ID */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Duda / Studio Sites</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={dudaLocationId}
                    onChange={(e) => setDudaLocationId(e.target.value)}
                    placeholder="Enter Location ID"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500"
                  />
                  <Button onClick={() => setDudaLocation(selectedUser)}>
                    Save
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Uses master API key. Only Location ID needed.</p>
              </div>

              {/* Usage Stats */}
              {userDetails.usage && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Usage</h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-gray-500">Blogs</p>
                      <p className="font-medium">{userDetails.usage.blogs_used || userDetails.usage.monthly_blogs_used || 0} / {userDetails.usage.blogs_limit || userDetails.usage.monthly_blogs_limit || 5}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-gray-500">Images</p>
                      <p className="font-medium">{userDetails.usage.images_used || userDetails.usage.monthly_images_used || 0} / {userDetails.usage.images_limit || userDetails.usage.monthly_images_limit || 5}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-gray-500">Searches</p>
                      <p className="font-medium">{userDetails.usage.searches_used || userDetails.usage.monthly_searches_used || 0} / {userDetails.usage.searches_limit || userDetails.usage.monthly_searches_limit || 3}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Blogs */}
              {userDetails.blogs?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Recent Posts ({userDetails.blogs.length})</h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {userDetails.blogs.slice(0, 5).map((blog, i) => (
                      <div key={i} className="text-sm p-2 bg-gray-50 rounded">
                        <p className="font-medium truncate">{blog.title}</p>
                        <p className="text-gray-500 text-xs">{blog.generated_at ? new Date(blog.generated_at).toLocaleDateString() : '-'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <Button
                  onClick={() => impersonateUser(selectedUser)}
                  variant="outline"
                  className="flex-1"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Login as User
                </Button>
                {userDetails.user?.plan !== 'free' && (
                  <Button
                    onClick={() => {
                      setShowUserModal(false);
                      openCancelModal(userDetails.user);
                    }}
                    variant="outline"
                    className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Subscription
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && userToCancel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Cancel Subscription</h2>
            <p className="text-gray-600 mb-4">
              Are you sure you want to cancel the subscription for <strong>{userToCancel.name}</strong> ({userToCancel.email})?
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Current plan: <span className="font-semibold capitalize">{userToCancel.plan}</span>
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Why is this subscription being cancelled?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                rows={3}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCancelModal(false)}
                className="flex-1"
              >
                Keep Subscription
              </Button>
              <Button
                onClick={cancelSubscription}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
